import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import '../../services/ConsoleHelper.js';
import { buildApiUrl } from "../../services/apiConfig.js";
import { useUseCases } from '../../contexts/UseCaseContext';

import ChatHeader from './components/ChatHeader';
import ChatSuggestions from './components/ChatSuggestions';
import ChatMessages from './components/ChatMessages';
import ChatInput from './components/ChatInput';
import ConfirmationModal from './components/ConfirmationModal';

import useSocketConnection from './hooks/useSocketConnection';
import useVoiceRecording from './hooks/useVoiceRecording';

import chatbotService from './api';

import { styleText } from './utils/textFormatting';
import { 
  updateLastBotMessage, 
  updateLastBotMessageWithData, 
  addBotMessage, 
  addFileMessage 
} from './utils/messageHelpers';
import { createActionHandler } from './utils/actionHandlers';
import { 
  CUSTOM_USE_CASE_ROUTES,
  USE_CASE_ROLE_RESTRICTIONS,
  TOOL_IDS,
  ERROR_MESSAGES,
  ACTION_TYPES,
  STATUS_MESSAGES,
  TIMING,
  COLLECTION_NAME_PREFIX, 
  ID_LENGTH, 
  ID_CHARS,
  USER_ROLES,
  generateShortId
} from './constants';

// Filter use cases based on user role
const filterUseCasesByRole = (useCases, isAdmin, isAdvisor) => {
  const userRole = isAdmin ? 'admin' : (isAdvisor ? 'advisor' : 'user');
  
  return useCases.filter(uc => {
    const allowedRoles = USE_CASE_ROLE_RESTRICTIONS[uc.name];
    
    // If no restriction is defined, use case is visible to all
    if (!allowedRoles) return true;
    
    // Check if user's role is in the allowed list
    return allowedRoles.includes(userRole);
  });
};

// Dynamic suggestions based on available use cases
const getSuggestions = (isAdmin, isAdvisor, useCases) => {
  if (!useCases || useCases.length === 0) {
    return []; // Return empty if use cases haven't loaded yet
  }
  
  // Filter use cases by role first
  const roleFilteredUseCases = filterUseCasesByRole(useCases, isAdmin, isAdvisor);
  
  // Show only 2 suggestions for cleaner UI, plus Financial Sentiment Analysis
  return [...roleFilteredUseCases.slice(0, 2).map(uc => uc.name), "Financial Sentiment Analysis"];
};

const generateCollectionName = () => {
  let result = COLLECTION_NAME_PREFIX;
  for (let i = 0; i < ID_LENGTH; i++) {
    result += ID_CHARS.charAt(Math.floor(Math.random() * ID_CHARS.length));
  }
  return result;
};

const ChatbotScreen = ({ user_id: propUserId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { useCases, getUseCaseRoute } = useUseCases();
  const chatMessages = location.state?.chatMessages;
  const passedUserId = location.state?.user_id;

  const [sessionId] = useState(() => uuidv4());

  // If we navigated here after opening a use case, start with a clean slate
  const chatbotShouldReset = !!sessionStorage.getItem('chatbot_clear_history');
  if (chatbotShouldReset) sessionStorage.removeItem('chatbot_clear_history');

  const [hasStarted, setHasStarted] = useState(
    () => !chatbotShouldReset && !!(chatMessages && chatMessages.length > 0)
  );

  const [messages, setMessages] = useState(
    () => chatbotShouldReset ? [] : ((chatMessages && Array.isArray(chatMessages) && chatMessages.length > 0)
      ? chatMessages : [])
  );

  const [input, setInput] = useState("");
  const [files, setFiles] = useState([]);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdvisor, setIsAdvisor] = useState(false);
  const [isInvestmentBanker, setIsInvestmentBanker] = useState(
    () => sessionStorage.getItem('role')?.toLowerCase() === 'investment banker'
  );
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [confirmationData, setConfirmationData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [documentAnalysis, setDocumentAnalysis] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [, setClarificationType] = useState(null);
  const [originalPrompt, setOriginalPrompt] = useState("");
  const [pendingEnhance, setPendingEnhance] = useState(null);

  // Socket connection and voice recording hooks
  const { socketRef, transcriptionLogRef, transcriptionTextRef } = useSocketConnection(setInput, setIsRecording);
  const { audioLevel, handleStartRecording, handleStopRecording } = useVoiceRecording(
    socketRef,
    transcriptionLogRef,
    transcriptionTextRef,
    isRecording,
    setIsRecording
  );

  useEffect(() => {
    // Priority: prop > location state > sessionStorage
    const finalUserId = propUserId || passedUserId || sessionStorage.getItem('user_id');
    
    if (!finalUserId) {
      console.error('No user ID found. Redirecting to login...');
      navigate('/a3-login');
      return;
    }
    
    setUserId(finalUserId);

    const userRole = sessionStorage.getItem('role');
    setIsAdmin(userRole === USER_ROLES.ADMIN);
    setIsAdvisor(userRole === USER_ROLES.ADVISOR);
    setIsInvestmentBanker(userRole?.toLowerCase() === 'investment banker');

  }, [propUserId, passedUserId, navigate]);

  // Update conversation history when messages change
  useEffect(() => {
    const newHistory = messages.map(msg => ({
      role: msg.sender === "user" ? "user" : "assistant",
      content: msg.text || ""
    }));
    setConversationHistory(newHistory);
  }, [messages]);

  const handleFileUpload = async (event) => {
    const uploadedFiles = Array.from(event.target.files);

    // Intercept: user is uploading a file for SDV generation — show preview first
    if (pendingEnhance?.pendingAction === 'sdv_file_uploaded' && uploadedFiles.length > 0) {
      const file = uploadedFiles[0];
      const capturedCategory = pendingEnhance?.category;
      setPendingEnhance(null);
      setMessages(prev => [...prev,
        { text: `📎 ${file.name}`, sender: 'user' },
        { text: `Analysing <strong>${file.name}</strong>...`, sender: 'bot' },
      ]);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const resp = await fetch(`${buildApiUrl('EXPORT_USE_CASE')}/analyze-upload`, {
          method: 'POST',
          body: formData,
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.detail || 'Upload failed');

        const sheets = data.sheets_preview || [];
        const category = capturedCategory || file.name.replace(/\.[^.]+$/, '');

        const buildSheetHtml = (sheet, isFirst) => {
          const rowCount = sheet.total_rows || sheet.rows?.length || 0;
          const headerCells = sheet.columns.map(c =>
            `<th style="border:1px solid #d1d5db;padding:4px 8px;background:#f3f4f6;white-space:nowrap;font-size:11px;position:sticky;top:0;">${c}</th>`
          ).join('');
          const bodyRows = (sheet.rows || []).slice(0, 10).map(row =>
            `<tr>${sheet.columns.map(c => `<td style="border:1px solid #d1d5db;padding:4px 8px;white-space:nowrap;font-size:11px;">${row[c] ?? ''}</td>`).join('')}</tr>`
          ).join('');
          return `<details${isFirst ? ' open' : ''} style="margin-bottom:6px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
<summary style="padding:6px 10px;background:#f9fafb;cursor:pointer;font-size:12px;font-weight:600;list-style:none;display:flex;justify-content:space-between;">
  <span>${sheet.name}</span><span style="color:#6b7280;font-weight:400;">${rowCount} rows · ${sheet.columns.length} cols</span>
</summary>
<div style="overflow-x:auto;overflow-y:auto;max-height:260px;">
  <table style="border-collapse:collapse;width:100%;"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</div>
</details>`;
        };

        const sheetsHtml = sheets.map((sh, i) => buildSheetHtml(sh, i === 0)).join('');
        const totalRows = sheets.reduce((s, sh) => s + (sh.total_rows || sh.rows?.length || 0), 0);

        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: `<strong>${file.name}</strong> — ${totalRows} rows across ${sheets.length} sheet(s):<br/>${sheetsHtml}<br/>Ready to generate synthetic data?`,
            sender: 'bot',
            suggested_actions: [
              { text: 'Generate with SDV', action: 'sdv_from_upload', fileId: data.fileId, category, filename: file.name },
              { text: 'Cancel', action: 'start_over' },
            ],
          };
          return updated;
        });
      } catch (e) {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { text: `Upload failed: ${e.message}`, sender: 'bot' };
          return updated;
        });
      }
      return;
    }

    if (uploadedFiles && uploadedFiles.length > 0) {
      setFiles(uploadedFiles);

      const newCollectionName = generateCollectionName();
      setCollectionName(newCollectionName);

      const fileNames = uploadedFiles.map(file => file.name).join(", ");
      const uploadMessage = uploadedFiles.length > 1 
        ? STATUS_MESSAGES.UPLOADED_MULTIPLE 
        : STATUS_MESSAGES.UPLOADED_SINGLE;

      setMessages((prevMessages) => 
        addBotMessage(
          addFileMessage(prevMessages, uploadedFiles.length, fileNames),
          uploadMessage
        )
      );

      try {
        const analysisResult = await chatbotService.analyzeDocuments(
          uploadedFiles,
          newCollectionName,
          input || "Analyze these documents"
        );

        setDocumentAnalysis(analysisResult.analysis);
        console.log("Document analysis:", analysisResult.analysis);
      } catch (error) {
        console.error("Error analyzing documents:", error);
      }

      if (!hasStarted) setHasStarted(true);
      setFileUploaded(true);

      // Reset conversation context when uploading new files
      setConversationId(null);
      setClarificationType(null);
    }
  };

  const handleSendMessage = async () => {
    if (input.trim() === "" && files.length === 0) return;

    // Intercept: user is responding to a pending chat prompt (enhance or augment)
    if (pendingEnhance && input.trim()) {
      const instruction = input.trim();
      const capturedPending = pendingEnhance;
      setMessages(prev => [...prev, { text: instruction, sender: 'user' }]);
      setInput('');
      setPendingEnhance(null);
      const pendingAction = capturedPending.pendingAction || 'enhance_do_regenerate';
      const handler = createActionHandler(
        { ...capturedPending, action: pendingAction, instruction },
        { setInput, handleSendMessage, navigate, setConversationId, setClarificationType, setOriginalPrompt, setMessages, fileUploaded, collectionName, messages, conversationHistory, setPendingEnhance }
      );
      handler();
      return;
    }

    if (isRecording) {
      handleStopRecording();
    }

    if (!hasStarted) setHasStarted(true);

    const newMessages = [...messages, { text: input || "Please analyze these documents", sender: "user" }];
    setMessages(newMessages);
    setInput("");
    setIsBotTyping(true);

    try {
      setMessages(prev => addBotMessage(prev, STATUS_MESSAGES.PROCESSING));

      let response;

      if (files.length > 0) {
        try {
          const newCollectionName = collectionName || generateCollectionName();
          console.log(`Uploading files to collection: ${newCollectionName}`);
          setCollectionName(newCollectionName);

          await chatbotService.uploadFiles(files, newCollectionName);

          const messageText = input || `Please analyze the documents I've uploaded`;

          if (conversationId) {
            response = await chatbotService.updateConversation(messageText, userId, conversationId, newCollectionName);
          } else {
            const options = {
              collection_name: newCollectionName,
              conversation_history: conversationHistory,
              session_id: sessionId
            };
            response = await chatbotService.sendTextMessage(messageText, userId, options);
          }
        } catch (uploadError) {
          console.error("Upload error:", uploadError);

          if (uploadError.message && uploadError.message.includes(ERROR_MESSAGES.MILVUS)) {
            setMessages(prev => updateLastBotMessage(prev, ERROR_MESSAGES.DOCUMENT_PROCESSING));

            const fallbackMessage = `${input || "I uploaded some documents but couldn't process them. Please help with: "} ${files.map(f => f.name).join(", ")}`;

            if (conversationId) {
              response = await chatbotService.updateConversation(fallbackMessage, userId, conversationId);
            } else {
              const options = { conversation_history: conversationHistory, session_id: sessionId };
              response = await chatbotService.sendTextMessage(fallbackMessage, userId, options);
            }
          } else {
            throw new Error(`${ERROR_MESSAGES.UPLOAD_FAILED}: ${uploadError.message}`);
          }
        }
      } else if (fileUploaded && collectionName) {
        try {
          if (conversationId) {
            console.log(`Continuing conversation ${conversationId} with existing collection ${collectionName} and message: ${input}`);
            response = await chatbotService.updateConversation(input, userId, conversationId, collectionName);
          } else {
            const options = {
              collection_name: collectionName,
              conversation_history: conversationHistory,
              session_id: sessionId
            };
            response = await chatbotService.sendTextMessage(input, userId, options);
          }
        } catch (error) {
          if (error.message && error.message.includes(ERROR_MESSAGES.MILVUS)) {
            console.warn("Falling back to regular processing due to Milvus error");

            if (conversationId) {
              response = await chatbotService.updateConversation(input, userId, conversationId);
            } else {
              const options = { conversation_history: conversationHistory, session_id: sessionId };
              response = await chatbotService.sendTextMessage(input, userId, options);
            }
          } else {
            throw error;
          }
        }
      } else {

        if (conversationId) {
          response = await chatbotService.updateConversation(input, userId, conversationId);

          if (response.action === ACTION_TYPES.CREATE || response.action === ACTION_TYPES.OPEN) {

            setConfirmationData(response);
            setShowConfirmation(true);

            setMessages(prev => {
              const updatedMessages = [...prev];

              if (response.action === "create") {
                const useCaseName = response.use_case?.name || "new use case";
                const agentCount = response.suggested_agents?.length || 0;
                updatedMessages[updatedMessages.length - 1] = {
                  text: styleText(`I can create a new use case "${useCaseName}" with ${agentCount} suggested agents. Would you like to proceed?`),
                  sender: "bot"
                };
              } else {
                const useCaseName = response.use_case_details?.name || "the requested use case";
                updatedMessages[updatedMessages.length - 1] = {
                  text: styleText(`I found an existing use case "${useCaseName}". Would you like to open it?`),
                  sender: "bot"
                };
              }

              return updatedMessages;
            });

            setConversationId(null);
            setClarificationType(null);
            setOriginalPrompt("");

            setIsBotTyping(false);
            return;
          }
        } else {
          const options = { conversation_history: conversationHistory, session_id: sessionId };
          response = await chatbotService.sendTextMessage(input, userId, options);
        }
      }

      console.log("API response:", JSON.stringify(response, null, 2));

      if (response.ERROR && typeof response.ERROR === 'string') {
        console.error("API returned error:", response.ERROR);
        if (response.ERROR.includes('quota') || response.ERROR.includes('429')) {
          throw new Error(ERROR_MESSAGES.QUOTA_EXCEEDED);
        }
        throw new Error(response.ERROR);
      }

      if (response.status === STATUS_MESSAGES.NEEDS_CLARIFICATION) {
        setMessages(prev => updateLastBotMessageWithData(prev, {
          text: styleText(response.response),
          suggested_actions: response.suggested_actions,
          clarification_type: response.clarification_type
        }));

        if (response.conversation_id) {
          console.log(`Setting conversation ID to: ${response.conversation_id}`);
          setConversationId(response.conversation_id);
        }
        setClarificationType(response.clarification_type);

        if (!originalPrompt && response.original_prompt) {
          setOriginalPrompt(response.original_prompt);
        }

        setIsBotTyping(false);
        return;
      }

      if ((response.action === ACTION_TYPES.CREATE || response.action === ACTION_TYPES.OPEN) && response.needs_confirmation) {


        if (documentAnalysis && (files.length > 0 || fileUploaded)) {
          if (response.action === ACTION_TYPES.CREATE && response.suggested_agents) {
            const ragAgentIndex = response.suggested_agents.findIndex(agent => agent.tool_id === TOOL_IDS.RAG_AGENT);

            if (ragAgentIndex >= 0) {
              response.suggested_agents[ragAgentIndex].name = documentAnalysis.name;
              response.suggested_agents[ragAgentIndex].tool_prompt = documentAnalysis.description;
              response.suggested_agents[ragAgentIndex].collection_name = collectionName;
            } else {
              response.suggested_agents.push({
                id: generateShortId(),
                name: documentAnalysis.name,
                tool_id: TOOL_IDS.RAG_AGENT,
                tool_prompt: documentAnalysis.description,
                use_case_id: response.action === ACTION_TYPES.CREATE ? response.use_case.id : response.use_case_id,
                collection_name: collectionName
              });
            }

            response.suggested_agents = response.suggested_agents.map(agent => ({
              ...agent,
              collection_name: agent.tool_id === "00006" ? collectionName : ""
            }));
          }
        } else {
          if (response.action === "create" && response.suggested_agents) {
            response.suggested_agents = response.suggested_agents.map(agent => ({
              ...agent,
              collection_name: ""
            }));
          }
        }

        setConfirmationData(response);
        setShowConfirmation(true);

        setConversationId(null);
        setClarificationType(null);
        setOriginalPrompt("");

        setIsBotTyping(false);
        return;
      }
      else if (response.response) {
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[updatedMessages.length - 1] = {
            text: styleText(response.response),
            sender: "bot",
            ...(response.suggested_actions && { suggested_actions: response.suggested_actions }),
          };
          return updatedMessages;
        });

        if (response.use_case_suggestion) {
          setConfirmationData(response.use_case_suggestion);
          setShowConfirmation(true);

          setConversationId(null);
          setClarificationType(null);
        }
      }
      else {
        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[updatedMessages.length - 1] = {
            text: styleText(response.response ? response.response : "I've processed your request."),
            sender: "bot",
            ...(response.query && { query: response.query }),
          };
          return updatedMessages;
        });

        if (response.graph_html) {
          setMessages(prev => [
            ...prev,
            {
              type: "iframe",
              url: buildApiUrl('NOVA', response.graph_html),
              sender: "bot",
            }
          ]);
        }
      }
    } catch (e) {
      console.error("Error in handleSendMessage:", e);
      setMessages(prev => updateLastBotMessage(prev, e.message || ERROR_MESSAGES.SERVER_ERROR));
    } finally {
      setIsBotTyping(false);

      if (files.length > 0) {
        setFiles([]);
        setFileUploaded(true);
      }
    }
  };

  const handleConfirmAction = async () => {
    setShowConfirmation(false);
    setIsBotTyping(true);

    try {
      const confirmData = {
        action: confirmationData.action,
        use_case_id: confirmationData.action === "open" ? confirmationData.use_case_id : undefined,
        use_case: confirmationData.action === "create" ? confirmationData.use_case : undefined,
        suggested_agents: confirmationData.action === "create" ? confirmationData.suggested_agents : undefined,
        confirmed: true,
        user_id: userId
      };

      if ((files.length > 0 || fileUploaded) && collectionName) {
        confirmData.collection_name = collectionName;
        confirmData.add_rag_agent = true;

        if (documentAnalysis) {
          confirmData.document_analysis = documentAnalysis;
        }
        if (confirmData.action === "create" && confirmData.suggested_agents) {
          confirmData.suggested_agents = confirmData.suggested_agents.map(agent => ({
            ...agent,
            collection_name: collectionName
          }));
        }
      } else {
        if (confirmData.action === "create" && confirmData.suggested_agents) {
          confirmData.suggested_agents = confirmData.suggested_agents.map(agent => ({
            ...agent,
            collection_name: ""
          }));
        }
      }

      const confirmResponse = await chatbotService.confirmUseCase(confirmData);

      if (confirmResponse.status === "success") {
        const responseMessage = confirmResponse.conversation_response ||
          (confirmationData.action === "create"
            ? `Your use case "${confirmationData.use_case.name}" has been created successfully. Redirecting you to the use case page...`
            : `Opening the requested use case. Redirecting you now...`);

        setMessages(prev => {
          const updatedMessages = [...prev];
          updatedMessages[updatedMessages.length - 1] = {
            text: styleText(responseMessage),
            sender: "bot"
          };
          return updatedMessages;
        });

        setTimeout(() => {
          const useCaseId = confirmationData.action === "create"
            ? confirmationData.use_case.id
            : confirmationData.use_case_id;
          sessionStorage.setItem('chatbot_clear_history', '1');
          window.location.href = `/use-case/${useCaseId}`;
        }, 2000);
      } else {
        throw new Error("Failed to process your request. Please try again.");
      }
    } catch (e) {
      console.error("Error in confirmation:", e);
      setMessages(prev => updateLastBotMessage(prev, e.message || ERROR_MESSAGES.CONFIRMATION_ERROR));
    } finally {
      setIsBotTyping(false);
      setConfirmationData(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationData(null);
    setMessages(prev => updateLastBotMessage(prev, styleText(ERROR_MESSAGES.CANCEL_MESSAGE)));
  };

  const handleSuggestionClick = async (suggestion) => {
    // Check for custom routes first (special pages like /authoring)
    const customRoute = CUSTOM_USE_CASE_ROUTES[suggestion];
    if (customRoute) {
      navigate(customRoute);
      return;
    }
    
    // Otherwise use dynamic route from context (standard use case dashboards)
    const route = getUseCaseRoute(suggestion);
    
    if (route) {
      navigate(route);
    } else {
      setInput(suggestion);
      setTimeout(() => handleSendMessage(), TIMING.ACTION_DELAY);
    }
  };

  const handleSuggestedActionClick = async (action) => {
    const handler = createActionHandler(action, {
      setInput,
      handleSendMessage,
      navigate,
      setConversationId,
      setClarificationType,
      setOriginalPrompt,
      setMessages,
      fileUploaded,
      collectionName,
      messages,
      conversationHistory,
      setPendingEnhance,
    });

    handler();
  };

  return (
    <div className="flex flex-col min-h-screen relative">
      {/* Aurora mesh background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0" style={{ background: '#f0f4ff' }}>
        <div className="aurora-orb aurora-orb-1" />
        <div className="aurora-orb aurora-orb-2" />
        <div className="aurora-orb aurora-orb-3" />
        <div className="aurora-orb aurora-orb-4" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col min-h-screen">

        {/* Investment Banker trigger notification */}
        {isInvestmentBanker && (
          <div className="relative z-10 flex items-center justify-between gap-3 px-4 py-2.5 w-full max-w-[800px] mx-auto mt-3 rounded-xl bg-amber-50 border border-amber-200 shadow-sm">
            <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-500 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11a6 6 0 0 0-5-5.91V4a1 1 0 0 0-2 0v1.09A6 6 0 0 0 6 11v5l-2 2v1h16v-1l-2-2z"/>
              </svg>
              New triggers came up for ABC Corp - take a look!
            </div>
            <button
              onClick={() => navigate('/pre-call-summary')}
              className="shrink-0 px-3 py-1 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
            >
              Take a look
            </button>
          </div>
        )}

        <main className="w-full flex items-center justify-center flex-1 p-3 sm:p-5 transition-all duration-300 ease-in-out overflow-x-hidden">
          <div className="w-full max-w-[900px] min-w-0 flex flex-col items-center p-4 sm:p-8 rounded-[24px] transition-all duration-300 ease-in-out" >
            <ChatHeader />

            {/* Only show suggestions when conversation hasn't started */}
            {!hasStarted && messages.length === 0 && (
              <ChatSuggestions
                suggestions={[...new Set([...getSuggestions(isAdmin, isAdvisor, useCases), "Fraud Claim Intake", "Pre-Call Summary"])]}
                handleSuggestionClick={handleSuggestionClick}
              />
            )}

            {hasStarted && messages.length > 0 && (
              <ChatMessages
                messages={messages}
                isBotTyping={isBotTyping}
                onSuggestedActionClick={handleSuggestedActionClick}
              />
            )}

            <ChatInput
              input={input}
              setInput={setInput}
              handleSendMessage={handleSendMessage}
              handleFileUpload={handleFileUpload}
              isRecording={isRecording}
              audioLevel={audioLevel}
              handleStartRecording={handleStartRecording}
              handleStopRecording={handleStopRecording}
              fileUploaded={fileUploaded}
              placeholder={pendingEnhance
                ? (pendingEnhance.pendingAction === 'connect_db_submit'
                    ? "e.g. MSSQL, myserver.database.windows.net, 1433, MyDB, myuser, mypassword"
                    : pendingEnhance.pendingAction === 'augment_do_add'
                    ? "Describe what you want to add or change..."
                    : "Describe what you want to change...")
                : undefined}
            />

            {/* Confirmation modal for use case actions */}
            <ConfirmationModal
              isOpen={showConfirmation}
              onClose={handleCancelAction}
              onConfirm={handleConfirmAction}
              action={confirmationData?.action}
              useCaseDetails={confirmationData?.action === "open" ? confirmationData?.use_case_details : confirmationData?.use_case}
              suggestedAgents={confirmationData?.suggested_agents}
              hasUploadedFiles={files.length > 0 || (fileUploaded && collectionName)}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ChatbotScreen;

